/**
 * DocumentReader — the paginated agreement, and the gate that says it was read.
 *
 * ── THE TWO GATES ─────────────────────────────────────────────────────────────
 * "Continue" opens only when BOTH are satisfied:
 *
 *   · REACH THE END — every page up to the last has been turned to. Scrolling is not
 *     enough on a phone, but turning to the final page is a deliberate act.
 *   · SPEND THE TIME — a floor on seconds actually spent on this step. The floor is
 *     `secondsFloor` (the ceremony passes ~45s), counted only while the tab is visible,
 *     so a document left open in a background tab does not "read itself".
 *
 * Neither gate is a claim that the document was understood. They are the smallest honest
 * evidence that it was in front of the signer, and they are the pair the executed audit
 * record has to be able to stand behind.
 *
 * The page HTML arrives already parsed by `agreement-markdown.ts` and is sanitised here
 * before it is mounted.
 *
 * ── AND THE SAME PAGES WITH NEITHER GATE ──────────────────────────────────────
 * `DocumentReaderReference` is the second export: the same article and the same pager, with
 * no timer, no gate sentence and nothing to satisfy. It is what the at-rest viewer shows —
 * a driver reading an instrument they have already signed is not being asked to prove they
 * read it, and a countdown on that screen would be theatre.
 *
 * TWO COMPONENTS RATHER THAN A `reference` FLAG, because the gates are not a decoration on a
 * reader: they are what the gated one IS. A boolean would put "the evidence the executed
 * audit record stands behind" and "a page-turner" behind the same call site, and the day one
 * grows a prop the other would carry it too. The shared half is `ReaderPages`, below.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Lock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { sanitizeAgreementHtml } from '@/lib/sanitizeAgreementHtml';
import { cn } from '@/lib/utils';

interface Props {
    /** One HTML string per page, from `agreementMarkdownToPages`. */
    pages: string[];
    page: number;
    onPageChange: (page: number) => void;
    /** Highest page number reached so far, persisted by the ceremony. */
    pagesSeen: number;
    /** Seconds already spent on this step across visits. */
    readSeconds: number;
    onReadSecondsChange: (seconds: number) => void;
    secondsFloor: number;
    /** Shown above the first page when the text is frozen. */
    frozenNotice?: string | null;
    className?: string;
}

export default function DocumentReader({
    pages,
    page,
    onPageChange,
    pagesSeen,
    readSeconds,
    onReadSecondsChange,
    secondsFloor,
    frozenNotice,
    className,
}: Props) {
    const total = Math.max(1, pages.length);
    const current = Math.min(Math.max(1, page), total);
    const [tick, setTick] = useState(readSeconds);
    const tickRef = useRef(readSeconds);
    const onSecondsRef = useRef(onReadSecondsChange);
    useEffect(() => {
        onSecondsRef.current = onReadSecondsChange;
    }, [onReadSecondsChange]);

    // One interval for the life of the step. It stops counting at the floor, and skips any
    // tick where the tab is hidden — a document left open in a background tab does not read
    // itself, and a floor that a backgrounded tab satisfies is not a floor.
    useEffect(() => {
        const timer = window.setInterval(() => {
            if (document.visibilityState !== 'visible') return;
            if (tickRef.current >= secondsFloor) return;
            tickRef.current += 1;
            setTick(tickRef.current);
            onSecondsRef.current(tickRef.current);
        }, 1000);
        return () => window.clearInterval(timer);
    }, [secondsFloor]);

    const remaining = Math.max(0, secondsFloor - tick);

    return (
        <div className={cn('space-y-4', className)}>
            <ReaderPages
                pages={pages}
                page={current}
                total={total}
                onPageChange={onPageChange}
                notice={frozenNotice}
            />

            <p className="text-center text-[11.5px] text-muted-foreground">
                {pagesSeen < total
                    ? `Reach page ${total} to continue.`
                    : remaining > 0
                        ? `You have reached the end. ${remaining}s left of the minimum reading time.`
                        : 'You have reached the end of the document.'}
            </p>
        </div>
    );
}

/**
 * The same document, with nothing to satisfy — the at-rest reader.
 *
 * No timer, no page-reach gate, no sentence telling the reader what is left to do, and no
 * download affordance of any kind: this is a driver looking at something they have already
 * signed, or at the standing Terms their Schedule incorporates.
 */
export function DocumentReaderReference({
    pages,
    page,
    onPageChange,
    notice,
    className,
}: {
    pages: string[];
    page: number;
    onPageChange: (page: number) => void;
    /** The line above the first page, where there is something worth pinning there. */
    notice?: string | null;
    className?: string;
}) {
    const total = Math.max(1, pages.length);
    const current = Math.min(Math.max(1, page), total);

    return (
        <div className={cn('space-y-4', className)}>
            <ReaderPages
                pages={pages}
                page={current}
                total={total}
                onPageChange={onPageChange}
                notice={notice}
            />
        </div>
    );
}

/** The article and the pager, which are the same in both readers. */
function ReaderPages({
    pages,
    page,
    total,
    onPageChange,
    notice,
}: {
    pages: string[];
    page: number;
    total: number;
    onPageChange: (page: number) => void;
    notice?: string | null;
}) {
    const html = useMemo(() => sanitizeAgreementHtml(pages[page - 1] ?? ''), [pages, page]);

    // Every page change returns the reader to the top of the new page.
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, [page]);

    return (
        <>
            {notice && (
                <div className="flex items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2 text-[11.5px] text-muted-foreground">
                    <Lock className="h-3.5 w-3.5 shrink-0" />
                    <span>{notice}</span>
                </div>
            )}

            <article
                className="agreement-prose rounded-xl border border-border bg-card px-5 py-6 shadow-sm"
                // Parsed by agreement-markdown.ts with html:false, then DOMPurified above.
                dangerouslySetInnerHTML={{ __html: html }}
            />

            {/* ALWAYS RENDERED, on a one-page document too: the pager is where "Page 1 of 1"
                is stated, and a reader that silently drops it leaves somebody wondering
                whether there is more below the fold. Both buttons simply sit disabled. */}
            <div className="flex items-center justify-between gap-3">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => onPageChange(page - 1)}
                >
                    <ChevronLeft className="h-4 w-4" />
                    Prev
                </Button>
                <span className="text-xs tabular-nums text-muted-foreground">
                    Page {page} of {total}
                </span>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page >= total}
                    onClick={() => onPageChange(page + 1)}
                >
                    Next
                    <ChevronRight className="h-4 w-4" />
                </Button>
            </div>
        </>
    );
}
